import React, { useState } from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Slider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  TextField,
  Card,
  Divider
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useConfig } from "../../contexts/ConfigContext";
import { configGenerators, validateConfiguration } from "../../utilities/configGenerators";
import { getCacheStatisticsReport, clearCache } from "../../utilities/relatedItemsCache";
import { useAuthSelector } from "../../store/auth";
import { NAVBAR_HEIGHT } from "../../pages/Map";
import InfoIcon from "@mui/icons-material/Info";

interface AdminProps {
  onClose?: () => void;
}

const Admin: React.FC<AdminProps> = ({ onClose }) => {
  const config = useConfig();
  const { user: profile } = useAuthSelector();

  // State for configuration generators
  const [isGeneratingComplete, setIsGeneratingComplete] = useState<boolean>(false);
  const [configGenerationStatus, setConfigGenerationStatus] = useState<string>("");
  const [completeGeneratedConfig, setCompleteGeneratedConfig] = useState<string>("");

  // State for cache management
  const [cacheStats, setCacheStats] = useState<string>("");

  const handleExaggerationChange = (event: Event, newValue: number | number[]) => {
    config.updateConfig({ terrainExaggeration: newValue as number });
  };

  // Common configuration generation constants
  const CONFIG_GENERATION_DEFAULTS = {
    includeHiddenFields: false,
    prioritizeUserFriendlyLabels: true,
    includeMetadata: false,
    maxPopupFields: 8,
    includeAllForms: true,
    autoDetectRelationshipKeys: true
  };

  const copyCompleteConfigToClipboard = () => {
    navigator.clipboard.writeText(completeGeneratedConfig);
    setConfigGenerationStatus("📋 Complete configuration copied to clipboard!");
    setTimeout(() => setConfigGenerationStatus(""), 3000);
  };

  // Common error handling and validation logic (DRY improvement)
  const handleConfigurationResult = (config: any, successMessage: string) => {
    const validation = validateConfiguration(config);

    if (!validation.valid) {
      console.warn("Generated configuration validation warnings:", validation.errors);
      setConfigGenerationStatus(`⚠️ Configuration generated with warnings: ${validation.errors.join(", ")}`);
    } else {
      setConfigGenerationStatus(successMessage);
    }

    const configJson = JSON.stringify(config, null, 2);
    setCompleteGeneratedConfig(configJson);
  };

  // Common API key validation (DRY improvement)
  const validateApiKey = (): boolean => {
    if (!profile?.fulcrum_api_key) {
      setConfigGenerationStatus("❌ No Fulcrum API key available. Please configure your API key.");
      return false;
    }
    return true;
  };

  // Cache management functions
  const updateCacheStats = () => {
    const stats = getCacheStatisticsReport();
    setCacheStats(stats);
  };

  const handleClearCache = () => {
    clearCache();
    setCacheStats("Cache cleared successfully!");
    setTimeout(() => setCacheStats(""), 3000);
  };

  // Generate complete configuration using new generators (DRY refactored)
  const generateCompleteConfiguration = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setCompleteGeneratedConfig("");
    setConfigGenerationStatus("🚀 Generating complete configuration...");

    try {
      setConfigGenerationStatus("📊 Analyzing form fields and generating mappings...");

      const completeConfig = await configGenerators.generateComplete(profile, CONFIG_GENERATION_DEFAULTS);

      setConfigGenerationStatus("✅ Validating generated configuration...");
      handleConfigurationResult(completeConfig, "✅ Configuration generated and validated successfully!");

      console.log("🚀 Complete configuration generated:", {
        endpoints: completeConfig.apiEndpoints?.length || 0,
        relationships: Object.keys(completeConfig.reverseRelationships || {}).length,
        totalForms: completeConfig._analysis?.statistics?.totalForms || 0
      });
    } catch (error) {
      console.error("Error generating complete configuration:", error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };

  // Generate field importance analysis only (DRY refactored)
  const generateFieldAnalysis = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setConfigGenerationStatus("📊 Analyzing field importance...");

    try {
      const fieldConfig = await configGenerators.field.autoGenerateConfiguration(profile, {
        userProfile: profile,
        ...CONFIG_GENERATION_DEFAULTS,
        maxPopupFields: 10
      });

      handleConfigurationResult(fieldConfig, "✅ Field analysis completed!");
    } catch (error) {
      console.error("Error generating field analysis:", error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };

  // Generate relationship analysis only (DRY refactored)
  const generateRelationshipAnalysis = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setConfigGenerationStatus("🔗 Analyzing relationships...");

    try {
      const relationshipConfig = await configGenerators.relationship.generateAdminCompatibleConfig(profile);

      // For relationship config, we don't need full validation since it's a string
      setCompleteGeneratedConfig(relationshipConfig);
      setConfigGenerationStatus("✅ Relationship analysis completed!");
    } catch (error) {
      console.error("Error generating relationship analysis:", error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };

  const opacity = Math.round((config.backgroundOpacity || 0.4) * 255)
    .toString(16)
    .padStart(2, "0");

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: `calc(100dvh - ${NAVBAR_HEIGHT})`,
        position: "fixed",
        width: "100%",
        marginTop: NAVBAR_HEIGHT,
        inset: 0,
        backgroundColor: (theme) => `${theme.palette.secondary.main}${opacity}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999999
      }}
      tabIndex={0}
    >
      <Card
        sx={{
          width: {
            xs: "90%",
            md: "50dvw",
            lg: "35dvw"
          },
          maxWidth: "71rem", // 80% of 70rem
          minHeight: "240px", // 80% of 300px
          maxHeight: "72dvh", // 80% of 90vh
          backgroundColor: "grey.800",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          borderRadius: "0.5rem, 0.5rem, 0.5rem 0.5rem",
          borderTop: "6px solid",
          borderTopColor: "primary.main",
          transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            boxShadow: "0 12px 48px rgba(0, 0, 0, 0.4)"
          },
          color: "#fff",
          paddingY: "24px"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingX: "24px" }}>
          <Typography variant='h2' component='h2' sx={{ fontWeight: 600 }}>
            Application Settings
          </Typography>
          <IconButton aria-label='close' onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff", paddingX: "24px" }}>
          You can change options here and they should automatically update the behaviour of the application.
        </Typography>
        <Divider sx={{ backgroundColor: "grey.500", my: 16, marginX: "24px" }} />

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "24px", overflowY: "auto", height: "calc(72dvh - 120px)", paddingX: "24px" }}
        >
          {/* Related items cloud settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography variant='h5'>Related Items Cloud Settings</Typography>
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showMediaInRelatedItemsCloud ?? false}
                  onChange={(e) => config.updateConfig({ showMediaInRelatedItemsCloud: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Media in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showGeoInRelatedItemsCloud ?? false}
                  onChange={(e) => config.updateConfig({ showGeoInRelatedItemsCloud: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Geographic Items in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showIndirectRelationshipsInRing ?? false}
                  onChange={(e) => config.updateConfig({ showIndirectRelationshipsInRing: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Secondary Relationships in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
          </Box>

          {/* Data Grid Display Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'>Data Grid Display Settings</Typography>
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.hideCardsWithoutImages ?? false}
                  onChange={(e) => config.updateConfig({ hideCardsWithoutImages: e.target.checked })}
                  size='small'
                />
              }
              label={
                <>
                  <Typography sx={{ margin: 0 }}>Hide Cards Without Images</Typography>
                  <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff" }}>
                    Only show cards that have primary images for a cleaner presentation
                  </Typography>
                </>
              }
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.limitDataDrawerToExtent ?? true}
                  onChange={(e) => config.updateConfig({ limitDataDrawerToExtent: e.target.checked })}
                  size='small'
                />
              }
              label={
                <>
                  <Typography sx={{ margin: 0 }}>Limit Data Drawer to Map Extent</Typography>
                  <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff" }}>
                    Sort items visible on the map to the top of the data drawer and grey out the rest
                  </Typography>
                </>
              }
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
          </Box>

          {/*  3D Terrain Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'> 3D Terrain Settings</Typography>
            <Box
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                flexDirection: "column",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px",
                gap: "12px"
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", margin: 0, padding: 0 }}>
                <Typography id='terrain-exaggeration-slider'>Terrain Exaggeration</Typography>
                <Slider
                  aria-labelledby='terrain-exaggeration-slider'
                  value={config.terrainExaggeration || 4}
                  onChange={handleExaggerationChange}
                  step={0.5}
                  marks
                  min={0.5}
                  max={8}
                  valueLabelDisplay='auto'
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography textAlign='end'>Exaggeration</Typography>
                <Typography textAlign='end' sx={{ backgroundColor: "grey.600", paddingX: 12, paddingY: 4, borderRadius: "4px" }}>
                  {config.terrainExaggeration || 4}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "grey.600",
                  paddingX: "12px",
                  paddingY: "6px",
                  borderRadius: "4px",
                  width: "fit-content",
                  gap: 4
                }}
              >
                <InfoIcon sx={{ marginRight: 1.5 }} />
                <Typography textAlign='end'>Adjusts vertical exaggeration of terrain in 3D view</Typography>
              </Box>
            </Box>
          </Box>

          {/*  Interface Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'> Interface Settings</Typography>
            <Box
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                flexDirection: "column",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px",
                gap: "12px"
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", margin: 0, padding: 0 }}>
                <Typography id='terrain-exaggeration-slider'>Background Opacity</Typography>
                <Slider
                  aria-labelledby='background-opacity-slider'
                  value={config.backgroundOpacity || 0.4}
                  onChange={(event: Event, newValue: number | number[]) => config.updateConfig({ backgroundOpacity: newValue as number })}
                  step={0.01}
                  marks={[
                    { value: 0.0, label: "0%" },
                    { value: 0.2, label: "20%" },
                    { value: 0.4, label: "40%" },
                    { value: 0.6, label: "60%" },
                    { value: 0.8, label: "80%" },
                    { value: 1.0, label: "100%" }
                  ]}
                  min={0.0}
                  max={1.0}
                  valueLabelDisplay='auto'
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  sx={{
                    "& .MuiSlider-markLabel": {
                      color: "#fff"
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography textAlign='end'>Opacity</Typography>
                <Typography textAlign='end' sx={{ backgroundColor: "grey.600", paddingX: 12, paddingY: 4, borderRadius: "4px" }}>
                  {Math.round((config.backgroundOpacity || 0.4) * 100)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "grey.600",
                  paddingX: "12px",
                  paddingY: "6px",
                  borderRadius: "4px",
                  width: "fit-content",
                  gap: 4
                }}
              >
                <InfoIcon />
                <Typography textAlign='end'>Controls how much the page background overlays the map</Typography>
              </Box>
            </Box>
          </Box>

          {/* *********************************Developer tool  **************************** */}
          {profile?.is_staff && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box>
                <Box sx={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Typography variant='h5'> Developer Tools</Typography>
                  <Typography
                    sx={{
                      backgroundColor: "grey.600",
                      paddingX: "6px",
                      paddingY: "2px",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 4
                    }}
                  >
                    Staff Only <ManageAccountsIcon />
                  </Typography>
                </Box>
                <Typography variant='caption' sx={{ color: "#fff", mb: 2, display: "block" }}>
                  Tools for developers to analyze and configure the application (Staff Only)
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  margin: 0,
                  paddingY: "16px",
                  paddingX: "14px",
                  backgroundColor: "grey.700",
                  borderRadius: "8px",
                  gap: "12px"
                }}
              >
                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    backgroundColor: "transparent",
                    color: "#fff",
                    boxShadow: "none",
                    "&:before": { display: "none" }
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: 0,
                      minHeight: "unset",
                      "&.Mui-expanded": {
                        minHeight: "unset"
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                        "&.Mui-expanded": {
                          margin: 0
                        }
                      }
                    }}
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  >
                    <Typography variant='h6'> Configuration Generators</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    <Box>
                      <Typography variant='body2' sx={{ mb: 2, color: "#fff" }}>
                        Automatically generate CultureMap configurations from Fulcrum forms with intelligent field analysis and relationship
                        mapping.
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: "12px", mb: 2, flexWrap: "wrap" }}>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={generateCompleteConfiguration}
                        disabled={isGeneratingComplete}
                        size='small'
                      >
                        {isGeneratingComplete ? "⏳ Generating..." : "🚀 Complete Config"}
                      </Button>

                      <Button variant='outlined' onClick={generateFieldAnalysis} disabled={isGeneratingComplete} size='small'>
                        📊 Field Analysis
                      </Button>

                      <Button variant='outlined' onClick={generateRelationshipAnalysis} disabled={isGeneratingComplete} size='small'>
                        🔗 Relationships
                      </Button>

                      <Button variant='outlined' onClick={updateCacheStats} size='small'>
                        📈 Cache Stats
                      </Button>

                      <Button variant='outlined' color='warning' onClick={handleClearCache} size='small'>
                        🗑️ Clear Cache
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {configGenerationStatus && (
                        <Alert
                          severity={
                            configGenerationStatus.includes("❌") ? "error" : configGenerationStatus.includes("⚠️") ? "warning" : "info"
                          }
                          sx={{ mb: 2 }}
                        >
                          {configGenerationStatus}
                        </Alert>
                      )}

                      {cacheStats && (
                        <Alert severity='info' sx={{ mb: 2 }}>
                          <Typography variant='caption' component='pre' sx={{ whiteSpace: "pre-wrap" }}>
                            {cacheStats}
                          </Typography>
                        </Alert>
                      )}

                      {completeGeneratedConfig && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
                            <Typography variant='subtitle2'>Generated Configuration:</Typography>
                            <Button size='small' startIcon={<ContentCopyIcon />} onClick={copyCompleteConfigToClipboard}>
                              Copy
                            </Button>
                          </Box>
                          <TextField
                            multiline
                            rows={20}
                            value={completeGeneratedConfig}
                            fullWidth
                            variant='outlined'
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              "& .MuiInputBase-input": {
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                color: "#fff"
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#fff !important"
                              }
                            }}
                            InputProps={{ readOnly: true }}
                          />
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Admin;
